import { useState, useEffect, useRef, useCallback } from 'react';
import { __ } from '@wordpress/i18n';
import { PlayIcon } from '../../../../Utils/icons';

/**
 * Autoplay pause/play control for the TikTok carousel (WCAG 2.2.2 Pause, Stop,
 * Hide). SMASH-1382 v3 frontend (T5).
 *
 * Deliberately a SEPARATE component holding its own `isPaused` state: owl is
 * driven by react-owl-carousel, whose componentDidUpdate destroys + recreates
 * owl on every render of its parent. If the toggle's state lived in
 * CarouselLayout, each pause/play would re-init owl — restarting autoplay and
 * resetting the slide. Keeping the state here means toggling re-renders only
 * this button; the sibling <OwlCarousel> is untouched, so the autoplay stop
 * sticks and the current slide is preserved.
 *
 * owl is initialised against the global jQuery (window.jQuery — react-owl-
 * carousel's `var $ = window.jQuery`), so its autoplay events must be triggered
 * on that same instance, not a module-bundled copy.
 *
 * Per the ARIA carousel pattern, auto-rotation also stops when keyboard focus
 * enters the carousel and only restarts when the user activates this control.
 */
const getJQ = () => (typeof window !== 'undefined' ? window.jQuery : null);

const CarouselAutoplayControl = ({ carouselRef }) => {
    const [isPaused, setIsPaused] = useState(false);
    const toggleRef = useRef(null);

    const getOwlEl = useCallback(
        () => (carouselRef.current ? carouselRef.current.querySelector('.owl-carousel') : null),
        [carouselRef]
    );

    const pause = useCallback(() => {
        const el = getOwlEl();
        const jq = getJQ();
        if (el && jq) jq(el).trigger('stop.owl.autoplay');
        setIsPaused(true);
    }, [getOwlEl]);

    const play = useCallback(() => {
        const el = getOwlEl();
        const jq = getJQ();
        if (el && jq) jq(el).trigger('play.owl.autoplay');
        setIsPaused(false);
    }, [getOwlEl]);

    useEffect(() => {
        const root = carouselRef.current;
        if (!root) return;
        const onFocusIn = (e) => {
            // Don't undo the user activating the toggle itself.
            if (toggleRef.current && toggleRef.current.contains(e.target)) return;
            pause();
        };
        root.addEventListener('focusin', onFocusIn);
        return () => root.removeEventListener('focusin', onFocusIn);
    }, [carouselRef, pause]);

    return (
        <button
            type="button"
            ref={toggleRef}
            className="sbt-carousel-autoplay-toggle sb-button-reset"
            onClick={() => (isPaused ? play() : pause())}
            aria-label={isPaused
                ? __('Start automatic slide rotation', 'sb-customizer')
                : __('Pause automatic slide rotation', 'sb-customizer')}
            style={{
                position: 'absolute', top: '8px', right: '8px', zIndex: 20,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: '32px', height: '32px', borderRadius: '50%',
                border: 'none', padding: 0, background: 'rgba(0,0,0,0.55)', cursor: 'pointer',
            }}
        >
            {isPaused
                ? <PlayIcon />
                : <svg width="14" height="16" viewBox="0 0 18 20" aria-hidden="true" focusable="false">
                    <rect x="2" y="1" width="5" height="18" rx="1" fill="white" />
                    <rect x="11" y="1" width="5" height="18" rx="1" fill="white" />
                  </svg>
            }
        </button>
    );
};

export default CarouselAutoplayControl;
